import React from 'react';
import { Info, DollarSign, Calculator, PiggyBank, TrendingUp, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о сложных процентах и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о сложных процентах: принципы, расчеты, стратегии
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Что такое сложные проценты?
        </h3>
        <p>
          <strong>Сложные проценты</strong> — это механизм начисления процентов, при котором проценты начисляются не только на основную сумму вложения, но и на ранее начисленные проценты. Это один из самых мощных финансовых инструментов, который Альберт Эйнштейн назвал "восьмым чудом света".
        </p>
        <p>
          При использовании сложных процентов ваши инвестиции растут по экспоненте, а не линейно. Это означает, что со временем скорость роста увеличивается, особенно при длительных сроках инвестирования.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Как работают сложные проценты?
        </h3>
        <p>
          Допустим, вы инвестировали 100 000 ₽ под 10% годовых. По принципу <strong>простых процентов</strong> через 10 лет у вас будет: 100 000 ₽ + (100 000 ₽ × 10% × 10 лет) = 200 000 ₽.
        </p>
        <p>
          По принципу <strong>сложных процентов</strong> с ежегодной капитализацией через 10 лет сумма составит: 100 000 ₽ × (1 + 10%)^10 ≈ 259 374 ₽.
        </p>
        <p>
          Разница в почти 60 000 ₽ (или 30% дополнительной прибыли) возникает потому, что при сложных процентах доход от предыдущих периодов сам становится источником дохода.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Формула расчета сложных процентов</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mt-2">
            <strong>Основная формула: FV = PV × (1 + r)^n</strong><br />
            где:
          </p>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li><strong>FV</strong> (Future Value) — будущая стоимость инвестиций</li>
            <li><strong>PV</strong> (Present Value) — текущая стоимость (первоначальные инвестиции)</li>
            <li><strong>r</strong> — процентная ставка за период (в десятичной форме, например, 10% = 0,1)</li>
            <li><strong>n</strong> — количество периодов</li>
          </ul>
          <p className="text-blue-600 dark:text-blue-400 text-sm mt-2">
            <strong>С регулярными взносами: FV = PV × (1 + r)^n + PMT × ((1 + r)^n - 1) / r</strong><br />
            где <strong>PMT</strong> — сумма регулярного взноса
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Ключевые факторы роста инвестиций
        </h3>
        <h4>1. Первоначальный капитал</h4>
        <p>
          Чем больше начальная сумма инвестиций, тем выше абсолютный прирост. Однако даже небольшие регулярные вложения могут привести к значительным результатам благодаря силе сложных процентов.
        </p>
        
        <h4>2. Процентная ставка</h4>
        <p>
          Это один из самых важных факторов. Разница между ставками 7% и 10% может выглядеть небольшой, но за 20 лет она может привести к почти двукратной разнице в итоговой сумме.
        </p>
        
        <h4>3. Время инвестирования</h4>
        <p>
          Время — ключевой фактор при инвестировании по принципу сложных процентов. Чем дольше деньги работают, тем больше эффект снежного кома. Начало инвестирования на 10 лет раньше может удвоить или утроить конечный результат.
        </p>
        
        <h4>4. Частота капитализации</h4>
        <p>
          Чем чаще происходит капитализация (начисление процентов), тем выше эффективная годовая ставка. Ежемесячная капитализация дает более высокий результат, чем ежегодная при той же номинальной ставке.
        </p>
        
        <h4>5. Регулярные взносы</h4>
        <p>
          Добавление регулярных взносов к инвестициям значительно ускоряет их рост. Это особенно важно для тех, кто начинает с небольшого капитала.
        </p>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-orange-500" />
          Инвестиционные инструменты в России
        </h3>
        <p>
          В России существует несколько способов инвестировать средства, получая сложный процент:
        </p>
        <ul>
          <li><strong>Банковские вклады с капитализацией</strong> — самый простой и надежный, но обычно дающий более низкую доходность. Депозиты до 1,4 млн ₽ застрахованы АСВ.</li>
          <li><strong>Облигации</strong> — государственные (ОФЗ) или корпоративные. Купонный доход можно реинвестировать.</li>
          <li><strong>Паевые инвестиционные фонды (ПИФы)</strong> — коллективные инвестиции под управлением профессионалов.</li>
          <li><strong>ETF (биржевые инвестиционные фонды)</strong> — инвестиции в корзину активов, торгуются как акции.</li>
          <li><strong>Индивидуальные инвестиционные счета (ИИС)</strong> — специальные счета с налоговыми льготами.</li>
          <li><strong>Накопительное страхование жизни (НСЖ) и инвестиционное страхование жизни (ИСЖ)</strong> — комбинируют страховую защиту и накопление.</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Учет инфляции и налогов
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-2">
            <strong>Инфляция</strong> снижает покупательную способность денег со временем. Для расчета реальной доходности инвестиций нужно вычесть уровень инфляции из номинальной доходности.
          </p>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <strong>Налоги</strong> также уменьшают доходность инвестиций. В России базовая ставка НДФЛ на доходы от инвестиций составляет 13% для налоговых резидентов и 15% для нерезидентов. Для официальных долгосрочных инвестиций через ИИС можно получить налоговые вычеты.
          </p>
        </div>

        <h3>Стратегии инвестирования со сложными процентами</h3>
        <ol>
          <li><strong>Стратегия раннего старта</strong>: Начинайте инвестировать как можно раньше, даже с небольших сумм.</li>
          <li><strong>Метод регулярных инвестиций</strong>: Создайте автоматические регулярные пополнения инвестиционного счета.</li>
          <li><strong>Реинвестирование дивидендов</strong>: Все полученные проценты и дивиденды направляйте обратно в инвестиции.</li>
          <li><strong>Диверсификация</strong>: Распределяйте инвестиции между разными классами активов и инструментами.</li>
          <li><strong>Повышение финансовой грамотности</strong>: Постоянно изучайте основы инвестирования для принятия более взвешенных решений.</li>
        </ol>

        <h3>Правило 72 — простой способ оценки удвоения вложений</h3>
        <p>
          Чтобы примерно узнать, за сколько лет ваши деньги удвоятся при определенной процентной ставке, разделите число 72 на эту ставку. Например:
        </p>
        <ul>
          <li>При ставке 6% годовых: 72 ÷ 6 = 12 лет</li>
          <li>При ставке 8% годовых: 72 ÷ 8 = 9 лет</li>
          <li>При ставке 12% годовых: 72 ÷ 12 = 6 лет</li>
        </ul>
        <p>
          Это правило дает приблизительную оценку, но очень полезно для быстрого расчета в уме и понимания силы разных процентных ставок.
        </p>

        <h3>Как использовать наш калькулятор сложных процентов</h3>
        <p>
          Наш калькулятор позволяет:
        </p>
        <ul>
          <li>Рассчитать рост инвестиций с учетом сложных процентов</li>
          <li>Учесть регулярные взносы разной периодичности</li>
          <li>Выбрать частоту капитализации (ежемесячно, ежеквартально, ежегодно и т.д.)</li>
          <li>Скорректировать результаты с учетом инфляции</li>
          <li>Учесть налогообложение</li>
          <li>Сравнить разные варианты инвестирования</li>
          <li>Визуализировать рост капитала с помощью графиков</li>
        </ul>

        <p className="font-semibold mt-6">
          Сложные проценты — это ключевой инструмент для достижения финансовой независимости. 
          Начинайте инвестировать как можно раньше, регулярно пополняйте счет и используйте наш 
          калькулятор для планирования своего финансового будущего!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;